import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save, Gift, Calendar, RefreshCw, Target, Clock, Users, CalendarX } from 'lucide-react';
import { useReferralRequestSettings } from '@/hooks/useReferralRequestSettings';
import { toast } from 'sonner';

export default function ReferralRequestSettings() {
  const { t } = useTranslation();
  const { settings, isLoading, updateSettings, isUpdating } = useReferralRequestSettings();
  
  const [formData, setFormData] = useState({
    days_after_won: 7,
    max_attempts_per_cycle: 5,
    days_between_attempts: 0,
    cycle_repeat_days: 30,
    max_cycles: null as number | null,
    is_active: true,
    exclude_weekends: true,
    max_daily_calls_per_consultant: 5,
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        days_after_won: settings.days_after_won,
        max_attempts_per_cycle: settings.max_attempts_per_cycle,
        days_between_attempts: settings.days_between_attempts,
        cycle_repeat_days: settings.cycle_repeat_days,
        max_cycles: settings.max_cycles,
        is_active: settings.is_active,
        exclude_weekends: settings.exclude_weekends ?? true,
        max_daily_calls_per_consultant: settings.max_daily_calls_per_consultant ?? 5,
      });
    }
  }, [settings]);

  const handleSave = () => {
    updateSettings(formData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Gift className="h-8 w-8 text-primary" />
            Configurações de Pedido de Indicação
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure a automação de ligações para pedir indicações após fechamento de vendas
          </p>
        </div>
        <Button onClick={handleSave} disabled={isUpdating}>
          {isUpdating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Configurações
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Status do Sistema
            </CardTitle>
            <CardDescription>
              Ative ou desative o sistema de pedido de indicações
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Sistema Ativo</Label>
                <p className="text-sm text-muted-foreground">
                  Quando ativo, calls são criadas automaticamente
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Início do Ciclo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Início do Ciclo
            </CardTitle>
            <CardDescription>
              Quando iniciar o primeiro contato após o fechamento
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Dias após o WON</Label>
              <Input
                type="number"
                min={1}
                max={90}
                value={formData.days_after_won}
                onChange={(e) => setFormData(prev => ({ ...prev, days_after_won: parseInt(e.target.value) || 7 }))}
              />
              <p className="text-xs text-muted-foreground">
                A primeira ligação será agendada {formData.days_after_won} dias após o deal ser marcado como WON
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tentativas por Ciclo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Tentativas por Ciclo
            </CardTitle>
            <CardDescription>
              Quantas vezes tentar contato em cada ciclo (tentativas diárias)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Máximo de tentativas por ciclo</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={formData.max_attempts_per_cycle}
                onChange={(e) => setFormData(prev => ({ ...prev, max_attempts_per_cycle: parseInt(e.target.value) || 5 }))}
              />
              <p className="text-xs text-muted-foreground">
                Serão feitas até {formData.max_attempts_per_cycle} tentativas consecutivas (uma por dia útil)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Repetição de Ciclos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Repetição de Ciclos
            </CardTitle>
            <CardDescription>
              Configure a repetição do processo (ciclos infinitos)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Dias para repetir ciclo</Label>
              <Input
                type="number"
                min={7}
                max={180}
                value={formData.cycle_repeat_days}
                onChange={(e) => setFormData(prev => ({ ...prev, cycle_repeat_days: parseInt(e.target.value) || 30 }))}
              />
              <p className="text-xs text-muted-foreground">
                Após completar um ciclo, aguardar {formData.cycle_repeat_days} dias antes de iniciar novo ciclo
              </p>
            </div>
            <div className="bg-primary/10 p-3 rounded-lg">
              <p className="text-sm font-medium text-primary">♾️ Ciclos Infinitos</p>
              <p className="text-xs text-muted-foreground mt-1">
                O sistema continuará pedindo indicações indefinidamente, pois faz parte da rotina de relacionamento
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Configurações de Agendamento */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarX className="h-5 w-5" />
              Configurações de Agendamento
            </CardTitle>
            <CardDescription>
              Defina quando as ligações podem ser agendadas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Excluir Finais de Semana</Label>
                <p className="text-sm text-muted-foreground">
                  Não agendar ligações em sábados e domingos
                </p>
              </div>
              <Switch
                checked={formData.exclude_weekends}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, exclude_weekends: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Distribuição por Consultor */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Distribuição por Consultor
            </CardTitle>
            <CardDescription>
              Limite de ligações por dia para balanceamento
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Máximo de ligações por consultor/dia</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={formData.max_daily_calls_per_consultant}
                onChange={(e) => setFormData(prev => ({ ...prev, max_daily_calls_per_consultant: parseInt(e.target.value) || 5 }))}
              />
              <p className="text-xs text-muted-foreground">
                Distribui as ligações ao longo dos dias para não sobrecarregar nenhum consultor
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resumo Visual */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo do Fluxo</CardTitle>
          <CardDescription>
            Visualização de como o sistema funcionará com as configurações atuais
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-primary">Deal WON</span>
              <span>→</span>
              <span className="bg-primary/10 px-2 py-1 rounded">{formData.days_after_won} dias</span>
              <span>→</span>
              <span className="font-medium text-primary">1ª Ligação</span>
              <span className="text-muted-foreground">
                (se não atender: até {formData.max_attempts_per_cycle} tentativas diárias
                {formData.exclude_weekends ? ', apenas dias úteis' : ''})
              </span>
              <span>→</span>
              <span className="bg-primary/10 px-2 py-1 rounded">{formData.cycle_repeat_days} dias</span>
              <span>→</span>
              <span className="font-medium text-primary">Novo Ciclo</span>
              <span className="text-muted-foreground">
                (repete infinitamente ♾️)
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                📊 Máximo de {formData.max_daily_calls_per_consultant} ligações por consultor por dia para distribuição equilibrada
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
